/**
 * taskMapping
 *
 * Adapts a generic Task (from the task-engine API) to the RedDotTask shape
 * consumed by TaskDetailPanel.
 *
 * Only fields present on both types are mapped. Fields with no Task equivalent
 * (e.g. groupCode, triggerSource) are left undefined — TaskDetailPanel handles
 * absent optional fields gracefully.
 *
 * `code` drives clinical-guidance lookups inside TaskDetailPanel.
 *
 * `formDefinitionCode` is the Red Dot field that controls which clinical form
 * TaskDetailPanel opens. The backend writes this field (not `formDefinitionSlug`)
 * for Red Dot tasks, so it is preferred. `formDefinitionSlug` is kept as a
 * fallback for generic task-engine tasks.
 */

import type { Task } from '../types/tasks';
import type { RedDotTask } from '../services/api/redDotService';

export function taskToRedDotTask(task: Task): RedDotTask {
  return {
    id: task.id,
    name: task.name,
    type: task.type,
    status: task.status,
    description: task.description ?? undefined,
    // `code` drives clinical-guidance lookups and special action buttons inside
    // TaskDetailPanel (e.g. preference_assessment → PA navigation, fidelity_monitoring →
    // fidelity form). Null when the task is not a protocol task.
    code: task.code ?? undefined,
    assignedTo: task.assignedTo ?? undefined,
    dueDate: task.dueDate ?? undefined,
    createdAt: task.createdAt,
    // Prefer formDefinitionCode (set by the Red Dot backend) over formDefinitionSlug
    // (the generic task-engine field). For non-Red-Dot tasks both will be null.
    formDefinitionCode: (task.formDefinitionCode ?? task.formDefinitionSlug) ?? undefined,
    statusReason: task.statusReason ?? undefined,
    workflowInstanceId: task.workflowInstanceId ?? undefined,
    // Propagate the server-authoritative actor flag. Absent on list responses
    // (only the detail endpoint sets it); fail-safe undefined → false in TaskDetailPanel.
    canAct: task.canAct,
  };
}
